import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useState, useEffect } from 'react';
import BookDemo from './BookDemo';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

const CoverContainer = styled.div`
  position: relative;
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #0f0f23;
`;

const BackgroundImage = styled.div<{ imageUrl: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: ${props => props.imageUrl ? `url(${props.imageUrl})` : 'none'};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 1;
  transition: opacity 0.8s ease-in-out;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      to bottom,
      rgba(15, 15, 35, 0.4) 0%,
      rgba(15, 15, 35, 0.6) 50%,
      rgba(15, 15, 35, 0.9) 100%
    );
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 10;
  width: 100%;
  padding: 2rem;
  text-align: center;
`;

const SlideContent = styled.div<{ isActive: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) ${props => props.isActive ? 'translateY(0)' : 'translateY(30px)'};
  opacity: ${props => props.isActive ? 1 : 0};
  transition: all 0.6s ease-out;
  width: 100%;
  text-align: center;
  pointer-events: ${props => props.isActive ? 'auto' : 'none'};
`;

const SlideTitle = styled.h1`
  font-family: 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(2rem, 5vw, 4rem);
  font-weight: 700;
  color: #fff;
  margin: 0 0 1.5rem;
  line-height: 1.2;
  letter-spacing: -0.01em;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.7);
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
`;

const SlideSubtitle = styled.p`
  font-family: 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(1.125rem, 2.5vw, 1.5rem);
  font-weight: 400;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 auto 3rem;
  max-width: 800px;
  line-height: 1.6;
  letter-spacing: 0.02em;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.7);
`;

const CTAButton = styled.button`
  font-family: 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: transparent;
  color: #fff;
  border: 2px solid #fff;
  padding: 1.25rem 3rem;
  font-size: 1.125rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  margin-top: 2rem;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: #fff;
    transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: -1;
  }
  
  &:hover {
    color: #000;
    animation: ${pulse} 0.6s ease-in-out;
    
    &::before {
      left: 0;
    }
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const SliderContainer = styled.div`
  position: absolute;
  bottom: 4rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  width: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const SliderTrack = styled.div`
  position: relative;
  width: 100%;
  height: 3px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  overflow: hidden;
`;

const SliderProgress = styled.div<{ progress: number }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${props => props.progress}%;
  background: linear-gradient(90deg, rgba(255,255,255,0.9) 0%, #fff 100%);
  border-radius: 2px;
  transition: width 0.08s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.4);
`;

const SliderDots = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const SliderDot = styled.button<{ isActive: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: ${props => props.isActive ? '#fff' : 'rgba(255, 255, 255, 0.4)'};
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${props => props.isActive ? 'scale(1.5)' : 'scale(1)'};
  box-shadow: ${props => props.isActive ? '0 0 12px rgba(255, 255, 255, 0.6)' : 'none'};
  
  &:hover {
    background: rgba(255, 255, 255, 0.7);
    transform: ${props => props.isActive ? 'scale(1.6)' : 'scale(1.2)'};
    box-shadow: 0 0 8px rgba(255, 255, 255, 0.4);
  }
`;

const NavigationArrows = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 0 2rem;
  z-index: 15;
`;

const ArrowButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
    transform: scale(1.1);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

interface Slide {
  id: number;
  title: string;
  subtitle: string;
  backgroundImage: string;
}

const slides: Slide[] = [
  {
    id: 1,
    title: "Evidence-based platform that helps your mental health clinical team succeed",
    subtitle: "Harness the power of AI to deliver superior therapy outcomes while reducing administrative burden on your team.",
    backgroundImage: 'thinkable-cover-futuristic-comp.jpg'
  },
  {
    id: 2,
    title: "Centralizing clinical team efforts around one evidence-based platform",
    subtitle: "While therapists use varying techniques, Thinkable unifies your approach, measurement, and outcomes for consistent, effective care.",
    backgroundImage: 'therapist-laptop-notes.jpg'
  },
  {
    id: 3,
    title: "Beyond chat: game-like exercises that target maladaptive beliefs",
    subtitle: "Personalized, engaging interventions that adapt to each user's unique challenges and preferences for deeper therapeutic impact.",
    backgroundImage: 'person-using-thinkable.jpg'
  },
  {
    id: 4,
    title: "Mapping the inner monologue",
    subtitle: "Thinkable identifies thinking patterns behind each challenge, building personalized treatment tracks that address maladaptive beliefs and comorbidities.",
    backgroundImage: 'mapping-inner-monologue.jpg'
  }
];

interface CoverProps {
  ctaText?: string;
}

const Cover = ({ ctaText = "Book Demo" }: CoverProps) => {
  const [isDemoOpen, setIsDemoOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [progress, setProgress] = useState(0);

  // Get image URL with WordPress support
  const getImageUrl = (imageName: string) => {
    if (typeof window !== 'undefined' && (window as any).wpData?.themeUrl) {
      return `${(window as any).wpData.themeUrl}/build/${imageName}`;
    }
    return `./${imageName}`;
  };

  // Auto-advance slides
  useEffect(() => {
    const slideInterval = 6000; // 6 seconds per slide
    const updateInterval = 50; // Update progress every 50ms
    
    const progressTimer = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + (100 / (slideInterval / updateInterval));
        if (newProgress >= 100) {
          setCurrentSlide((prevSlide) => (prevSlide + 1) % slides.length);
          return 0;
        }
        return newProgress;
      });
    }, updateInterval);

    return () => clearInterval(progressTimer);
  }, []);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setProgress(0);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setProgress(0);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setProgress(0);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <>
      <CoverContainer>
        <BackgroundImage imageUrl={getImageUrl(currentSlideData.backgroundImage)} />
        
        <ContentWrapper>
          {slides.map((slide, index) => (
            <SlideContent key={slide.id} isActive={currentSlide === index}>
              <SlideTitle>{slide.title}</SlideTitle>
              <SlideSubtitle>{slide.subtitle}</SlideSubtitle>
              <CTAButton onClick={() => setIsDemoOpen(true)}>
                {ctaText}
              </CTAButton>
            </SlideContent>
          ))}
        </ContentWrapper>

        <NavigationArrows>
          <ArrowButton onClick={prevSlide}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
            </svg>
          </ArrowButton>
          <ArrowButton onClick={nextSlide}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
            </svg>
          </ArrowButton>
        </NavigationArrows>
        
        <SliderContainer>
          <SliderDots>
            {slides.map((_, index) => (
              <SliderDot
                key={index}
                isActive={currentSlide === index}
                onClick={() => goToSlide(index)}
              />
            ))}
          </SliderDots>
          <SliderTrack>
            <SliderProgress progress={progress} />
          </SliderTrack>
        </SliderContainer>
      </CoverContainer>
      <BookDemo isOpen={isDemoOpen} onClose={() => setIsDemoOpen(false)} />
    </>
  );
};

export default Cover;